import React, { useState, useEffect, useCallback } from "react";
import { fitActionColumn } from "../../../Utils/actionColumn";
import { Link, useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import ShowAllToggle from "../ShowAllToggle";
import EnrolledTerms, { FreeAssessmentTag, enrolledHeading } from "./EnrolledTerms";
import "react-data-table-component-extensions/dist/index.css";
import {
  member_destroy,
  members_page,
  impersonate_student,
} from "../../../apis/studentManagement/StudentManagement";
import { branch_all } from "../../../apis/Branches";
import { formatCourseNames, getUserPermission } from "../../../Utils/Utils";
import { STUDENT_PORTAL_URL } from "../../../constants/Matcher";

// The list is paged, searched and sorted on the server: after the CSOS import
// there are ~53k students, too many to load into the browser at once.

const customStyles = {
  table: { style: { tableLayout: "fixed" } },
  headRow: { style: { backgroundColor: "#f1f1f1", color: "#333", fontSize: "13px", minHeight: "36px" } },
  headCells: { style: { paddingLeft: "8px", paddingRight: "8px", paddingTop: "6px", paddingBottom: "6px", fontWeight: "bold", fontSize: "13px" } },
  rows: { style: { minHeight: "32px", paddingTop: "0px", paddingBottom: "0px", fontSize: "13px", backgroundColor: "#fff", "&:nth-child(odd)": { backgroundColor: "#f9f9f9" } } },
  cells: { style: { paddingTop: "4px", paddingBottom: "4px", paddingLeft: "8px", paddingRight: "8px" } },
  pagination: { style: { minHeight: "40px", fontSize: "13px" } },
};

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
};

const Members = () => {
  const navigate = useNavigate();
  const permissions = getUserPermission("Students");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [branchlist, setBranchList] = useState([]);

  const [branchId, setBranchId] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // Active students by default; "Show all" adds the inactive ones.
  const [showAll, setShowAll] = useState(false);
  const [sort, setSort] = useState({ field: "created_at", dir: "desc" });
  // flipping this tells the table to show page 1 again after a filter change
  const [resetPage, setResetPage] = useState(false);
  const toFirstPage = () => {
    setPage(1);
    setResetPage((v) => !v);
  };

  // wait for typing to pause before asking the server
  useEffect(() => {
    const t = setTimeout(() => {
      toFirstPage();
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    branch_all().then((res) => setBranchList(res?.data || []));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await members_page({
      id: branchId,
      page,
      limit: perPage,
      search,
      active: showAll ? "" : 1,
      sort: sort.field,
      dir: sort.dir,
    });
    setRows(res?.data?.rows || []);
    setTotal(res?.data?.total || 0);
    setLoading(false);
  }, [branchId, page, perPage, search, showAll, sort]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (id, status) => {
    const ok = window.confirm(
      Number(status) === 1
        ? "Are you sure you want to active this user?"
        : "Are you sure you want to inactive this user?"
    );
    if (ok) await member_destroy(id, { status });
    load();
  };

  const editStudent = (class_id, id) => {
    if (class_id !== null) {
      const ok = window.confirm(
        "This student is already enrolled in the class. Are you sure you want to proceed with editing it?"
      );
      if (!ok) return;
    }
    navigate(`/admin/students/member-edit/${id}`);
  };

  const handleImpersonate = async (studentUserId) => {
    const res = await impersonate_student(studentUserId);
    if (res?.status && res?.data) {
      const encoded = btoa(JSON.stringify(res.data));
      window.open(`${STUDENT_PORTAL_URL}/impersonate?data=${encoded}`, "_blank");
    }
  };

  const columns = [
    {
      name: "No.",
      cell: (row, index) => `#${(page - 1) * perPage + index + 1}`,
      width: "80px",
    },
    {
      name: "Name",
      sortable: true,
      sortField: "name",
      selector: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim(),
      cell: (r) => (
        <span>
          {`${r.first_name || ""} ${r.last_name || ""}`.trim()}
          {r.student_type === "0" ? <FreeAssessmentTag /> : null}
        </span>
      ),
    },
    {
      name: "Username",
      selector: (r) => r.user_name || "-",
    },
    {
      name: "Grade",
      sortable: true,
      sortField: "grade",
      selector: (r) =>
        r.grade_name === null || r.grade_name === undefined
          ? "-"
          : Number(r.grade_name) === 0
          ? "Year 0 (kindy)"
          : `Year ${r.grade_name}`,
    },
    {
      name: "Branch",
      sortable: true,
      sortField: "branch",
      selector: (r) => r.branch_name || "-",
    },
    {
      name: "Registered",
      sortable: true,
      sortField: "created_at",
      selector: (r) => formatDate(r.created_at),
    },
    {
      name: enrolledHeading,
      minWidth: "240px",
      cell: (r) => <EnrolledTerms enrolled={r.enrolled_terms} />,
    },
    {
      name: "Course",
      cell: (r) => (
        <div className="course-container">
          <span className="course-text">{formatCourseNames(r.course_name || "")}</span>
          {(r.course_name || "").split(", ").length > 1 && (
            <div className="tooltip-content">{r.course_name}</div>
          )}
        </div>
      ),
    },
    {
      name: "Status",
      sortable: true,
      sortField: "status",
      cell: (r) => (
        <select
          className="form-select"
          onChange={(e) => changeStatus(r.id, e.target.value)}
          value={r.status}
        >
          <option value={1}>Active</option>
          <option value={0}>Inactive</option>
        </select>
      ),
    },
    permissions?.edit === 1 || permissions?.delete === 1 || permissions?.view === 1
      ? {
          name: "Action",
          cell: (r) => (
            <div data-tag="allowRowEvents">
              {permissions?.view === 1 ? (
                <Link className="edit-btn" to={`/admin/students/student_enroll_view/${r.s_id}`}>
                  <i class="fa fa-eye" aria-hidden="true"></i>
                </Link>
              ) : (
                ""
              )}
              {permissions?.edit === 1 ? (
                r.class_id == null ? (
                  <Link className="edit-btn" to={`/admin/students/member-edit/${r.id}`}>
                    <i class="fa fa-pencil" aria-hidden="true"></i>
                  </Link>
                ) : (
                  <button className="dlt-btn" onClick={() => editStudent(r.class_id, r.id)}>
                    <i class="fa fa-pencil" aria-hidden="true"></i>
                  </button>
                )
              ) : (
                ""
              )}
              {permissions?.view === 1 && Number(r.status) === 1 ? (
                <button
                  className="dlt-btn"
                  title="Login as Student"
                  onClick={() => handleImpersonate(r.id)}
                >
                  <i class="fa fa-right-to-bracket" aria-hidden="true"></i>
                </button>
              ) : (
                ""
              )}
            </div>
          ),
        }
      : "",
  ].filter(Boolean);

  return (
    <div className="right-nav" id="dash-event">
      <div className="row">
        <div className="col-md-4">
          <h4 className="modal-title">Student List</h4>
        </div>
        <div className="col-md-8">
          {permissions?.add === 1 ? (
            <Link className="ui button float-end" to="/admin/students/add-member">
              Add Student
            </Link>
          ) : (
            ""
          )}
        </div>
      </div>
      <div className="col-12">
        <div className="form-group">
          <label>Branch</label>
          <select
            className="form-select form-control "
            name="branch"
            value={branchId}
            onChange={(e) => {
              toFirstPage();
              setBranchId(e.target.value);
            }}
          >
            <option value="">ALL BRANCHES</option>
            {branchlist.map((elem) => (
              <option key={elem?.id} value={elem?.id}>
                {elem?.branch_name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="searc-bar">
        <div className="row">
          <div className="col-md-6">
            <input
              type="search"
              className="form-control float-end"
              placeholder="Search name, username, email, school or branch..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <div className="col-md-6 text-end">
            <ShowAllToggle
              prefix={`${total.toLocaleString()} students`}
              checked={showAll}
              onChange={(value) => {
                toFirstPage();
                setShowAll(value);
              }}
            />
          </div>
        </div>
      </div>
      <DataTable
        noHeader
        columns={fitActionColumn(columns, rows)}
        data={rows}
        progressPending={loading}
        pagination
        paginationServer
        paginationTotalRows={total}
        paginationPerPage={perPage}
        paginationRowsPerPageOptions={[20, 50, 100, 200]}
        paginationResetDefaultPage={resetPage}
        onChangePage={(p) => setPage(p)}
        onChangeRowsPerPage={(n, p) => {
          setPerPage(n);
          setPage(p);
        }}
        sortServer
        defaultSortFieldId={6}
        defaultSortAsc={false}
        onSort={(column, direction) => {
          if (!column?.sortField) return;
          toFirstPage();
          setSort({ field: column.sortField, dir: direction });
        }}
        highlightOnHover
        customStyles={customStyles}
      />
    </div>
  );
};
export default Members;
